import React, { Fragment, useState, useEffect, useMemo } from 'react';
import styled, { useTheme, css, keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import faker from '@faker-js/faker';

import { Text, Box, Flex, List, SvgIcon } from 'Components/Base';
import { get as getTheme } from 'Components/Base/constants';
import { useClassroomCourseData } from 'Domain/Classroom';

const CourseSubsectionContainer = styled(Box)`
  cursor: pointer;
`;

const CourseSubsectionInnerWrapper = styled(Flex)`
  border-radius: 16px;
`;

const ContentText = (props) => (
  <Text
    textStyle={'body2'}
    fontFamily={'content'}
    fontWeight={'normal'}
    color="inherit"
    {...props}
  />
);

const CourseTOCSectionTitle = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  ${({ hasSubsectionActive }) =>
    hasSubsectionActive
      ? css`
          border-left: 4px solid ${getTheme('colors.primary')};
        `
      : css`
          border-left: 4px solid ${getTheme('colors.transparent')};
        `}
`;

const SubSectionArrowRotation = (subsectionState) => keyframes`
  0% {
    transform: ${subsectionState === 'active' ? 'rotate(180deg)' : 'rotate(0)'}
  };

  100% {
    transform: ${subsectionState === 'active' ? 'rotate(0)' : 'rotate(180deg)'}
  }
`;

const SubSectionArrow = styled(SvgIcon)`
  color: ${getTheme('colors.primary')};
  flex-shrink: 0;

  ${({ subsectionState }) =>
    subsectionState === 'inactive'
      ? css`
          transform: rotate(180deg);
        `
      : css`
          transform: rotate(0);
        `}

  animation: ${({ subsectionState }) =>
    SubSectionArrowRotation(subsectionState)} 300ms cubic-bezier(.13,.43,.93,.71);
`;

const getSubSectionColor = (complete, canPreview, active) => {
  if (active) {
    return {
      color: 'activeSubsectionSidebarText',
      bg: 'activeSubsectionSidebarBackground',
    };
  }
  if (complete) {
    return { bg: 'transparent', color: 'completedSubsectionSidebarText' };
  }
  if (!canPreview) {
    return { bg: 'notiGrey.grey3', color: 'sidebarSecondaryText' };
  }
  return {
    color: 'sidebarSecondaryText',
    bg: 'transparent',
  };
};

const CourseSection = ({ section, onSubsectionClick, defaultExpanded, name, ...props }) => {
  const { colors, space } = useTheme();
  const [isSubsectionOpen, setIsSubsectionOpen] = useState(defaultExpanded || false);
  const { subsections, title } = section;

  useEffect(() => {
    const indexOfActiveSubsection = subsections.findIndex((s) => s.active === true);
    if (indexOfActiveSubsection !== -1) {
      setIsSubsectionOpen(true);
    }
  }, []);

  const handleToggleSubsectionOpen = () => {
    setIsSubsectionOpen(!isSubsectionOpen);
  };

  const handleSubsectionClick = ({ subsection, section }) => {
    setIsSubsectionOpen(true);
    onSubsectionClick({ subsection, section });
  };

  const hasSubsectionActive = useMemo(() => {
    const subsection = subsections.find((subsection) => subsection?.active === true);
    if (subsection) return true;
    return false;
  }, [subsections]);

  return (
    <Box
      bg={'transparent'}
      style={{
        borderBottom: `1px solid ${colors.sidebarSecodaryDivider}`,
      }}
    >
      <CourseTOCSectionTitle
        data-testid={`${name}-tab`}
        px={[4, null, 7]}
        py={4}
        onClick={handleToggleSubsectionOpen}
        hasSubsectionActive={hasSubsectionActive}
      >
        <ContentText fontWeight="medium">{title}</ContentText>
        <SubSectionArrow
          size={24}
          subsectionState={isSubsectionOpen ? 'active' : 'inactive'}
          icon={SvgIcon.ICON_LIST.ARROW_UP}
        />
      </CourseTOCSectionTitle>

      {isSubsectionOpen && (
        <List
          items={subsections}
          renderItem={(subsection, index) => {
            const { subsectionIndex, active, complete, title, duration, id, canPreview } =
              subsection;
            const { color } = getSubSectionColor(complete, canPreview, active);

            return (
              <CourseSubsectionContainer
                key={id}
                data-testid={`${name}-subsection-${index + 1}-tab`}
                color={color}
                active={active}
                px={active ? [2, 2, 3] : [4, null, 7]}
                // py={active ? 0 : 4}
                bg={'transparent'}
                onClick={() => handleSubsectionClick({ subsection, section })}
              >
                <CourseSubsectionInnerWrapper
                  px={active ? [2, null, 5] : 0}
                  py={4}
                  active={active}
                  bg={active ? 'activeSubsectionSidebarBackground' : 'transparent'}
                >
                  <Box>
                    {active ? (
                      <SvgIcon
                        icon={SvgIcon.ICON_LIST.VIDEO_PLAY_CIRCLE}
                        size={24}
                        mr={5}
                        style={{ flexShrink: 0, height: '24px' }}
                      />
                    ) : complete ? (
                      <SvgIcon
                        icon={SvgIcon.ICON_LIST.CHECK_CIRCLE}
                        size={24}
                        color={'success'}
                        mr={5}
                        style={{ flexShrink: 0, height: '24px' }}
                      />
                    ) : (
                      <Text
                        color={'sidebarSecondaryText'}
                        minWidth={'24px'}
                        textAlign={'center'}
                        fontWeight="bold"
                        fontSize={2}
                        style={{ flexShrink: 0 }}
                        mr={5}
                      >
                        {subsectionIndex}
                      </Text>
                    )}
                  </Box>

                  <ContentText
                    style={{ flexGrow: 1 }}
                    lineHeight="inherit"
                    color={active ? 'text' : 'greyscale.grey3'}
                    pt={'2px'}
                  >
                    {title}
                  </ContentText>

                  <ContentText lineHeight="inherit">{duration}</ContentText>
                </CourseSubsectionInnerWrapper>
              </CourseSubsectionContainer>
            );
          }}
          columns={1}
          childSpacing={'0px'}
          rowSpacing={space[4]}
        />
      )}
    </Box>
  );
};

const TocWithRoundedHighlight = ({ sections = [], onSubsectionClick, ...props }) => {
  const { isClassroomTrialMode } = useClassroomCourseData();
  return (
    <Fragment>
      {sections.map(({ subSections = [], ...section }, index) => {
        const sectionWithIndex = {
          subsections: subSections.map(({ number, ...subSection }) => ({
            subsectionIndex: number,
            ...subSection,
          })),
          ...section,
        };
        return (
          <CourseSection
            key={section.id}
            name={`toc-with-rounded-highlight-section-${index + 1}`}
            section={sectionWithIndex}
            onSubsectionClick={onSubsectionClick}
            defaultExpanded={isClassroomTrialMode}
            {...props}
          />
        );
      })}
    </Fragment>
  );
};

CourseSection.propTypes = {
  name: PropTypes.string,
};

CourseSection.defaultProps = {
  name: faker.random.uuid(),
};

export default TocWithRoundedHighlight;
